import axios from "axios";
import type { ResolvedTrackEventInput } from "./types";

const EVENTS_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/events/event-data/insert`;

// Function to get customer ID from token at runtime
const getCustomerId = (): string => {
  try {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return "";

    const decodedToken = JSON.parse(atob(accessToken.split(".")[1]));
    return decodedToken && decodedToken["customer-id"]
      ? decodedToken["customer-id"]
      : "";
  } catch (error) {
    console.warn("Failed to decode access token:", error);
    return "";
  }
};

function stringifyPayload(payload: unknown): string {
  if (payload == null) return "{}";
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed.length ? trimmed : "{}";
  }
  try {
    const s = JSON.stringify(payload);
    return s && s.length ? s : "{}";
  } catch {
    return "{}";
  }
}

function inferOsFromUserAgent(): string {
  try {
    const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
    if (/android/.test(ua)) return "android";
    if (/iphone|ipod|ipad/.test(ua)) return "ios";
  } catch { }
  return "web";
}

function getUAETime(): string {
  // Get current time in UAE timezone (Asia/Dubai, UTC+4) in ISO 8601 format
  const date = new Date();
  // Convert to UAE time by getting the time string in Dubai timezone
  const uaeTimeString = date.toLocaleString('en-US', {
    timeZone: 'Asia/Dubai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Parse the UAE time string and create a new date
  const [datePart, timePart] = uaeTimeString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute, second] = timePart.split(':');

  // Create date in UAE timezone and return ISO string
  const uaeDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  const milliseconds = date.getMilliseconds().toString().padStart(3, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}Z`;
}

async function getCurrentLocation(): Promise<{ latitude: string; longitude: string }> {
  return new Promise((resolve) => {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      resolve({ latitude: "", longitude: "" });
      return;
    }

    // Try to get current position with timeout
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        });
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
        resolve({ latitude: "", longitude: "" });
      },
      {
        timeout: 5000, // 5 second timeout
        enableHighAccuracy: false, // Faster, less battery drain
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  });
}

// Function to get a deterministic browser fingerprint as a Device ID.
// This combines hardware and browser signals to create an ID that is 
// stable for the specific device/browser combination.
const getBrowserFingerprint = async (): Promise<string> => {
  try {
    const signals = [
      window.navigator.userAgent,
      window.navigator.language,
      window.navigator.platform,
      window.navigator.hardwareConcurrency || "unknown",
      (window.navigator as any).deviceMemory || "unknown",
      window.screen.width + "x" + window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      // Adding a canvas fingerprint for extra uniqueness
      (() => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return "";
        ctx.textBaseline = "top";
        ctx.font = "14px 'Arial'";
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = "#f60";
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = "#069";
        ctx.fillText("KirkosAnalytics", 2, 15);
        ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
        ctx.fillText("KirkosAnalytics", 4, 17);
        return canvas.toDataURL();
      })()
    ].join('###');

    const msgUint8 = new TextEncoder().encode(signals);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    // Fallback to a random ID if fingerprinting fails
    return "web-" + Math.random().toString(36).substring(2, 15);
  }
};

export async function trackEvent(input: ResolvedTrackEventInput): Promise<void> {
  // Fetch location and fingerprint in parallel to avoid sequential blocking
  const [location, fingerprint] = await Promise.all([
    getCurrentLocation(),
    getBrowserFingerprint()
  ]);

  const data = {
    event_timestamp: input.event_timestamp ?? getUAETime(),
    app_id: input.app_id ?? "",
    event_name: input.event_name,
    event_category: input.event_category,
    module: input.module ?? "",
    device_id: input.device_id || fingerprint || "web-unknown",
    os: input.os ?? inferOsFromUserAgent(),
    make: input.make ?? "",
    model: input.model ?? "",
    latitude: location.latitude,
    longitude: location.longitude,
    customer_id: getCustomerId(),
    merchant_id: input.merchant_id ?? "",
    kiosk_id: input.kiosk_id ?? "",
    payload: stringifyPayload(input.payload),
    install_referrer: input.install_referrer ?? "",
    campaign_id: input.campaign_id ?? "",
  };

  try {
    console.debug("[Analytics] trackEvent sending:", data);
    await axios.post(EVENTS_ENDPOINT, data, {
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    console.error("trackEvent(web) failed", err);
  }
}